(() => {
  // Same format source as u_FormatMoney in assets/_custom.js (shop.money_format).
  const formatMoney = (pence) => {
    const format = window.theme?.settings?.moneyWithoutCurrencyFormat || '£{{amount}}';
    return format.replace(/\{\{amount\}\}/g, (Number(pence) / 100).toFixed(2));
  };

  const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const readPackPrices = (root, variantId) => {
    const priceData = root.querySelector('[data-advent-pack-prices]');
    if (!priceData || !variantId) return null;
    try {
      return JSON.parse(priceData.textContent)[String(variantId)] || null;
    } catch (error) {
      console.error('Unable to read advent calendar pack prices.', error);
      return null;
    }
  };

  // Total in pence for any quantity, computed exactly like the main button's
  // label in assets/product-form.js (renderAddButtonPrice): the same pack rows,
  // row flags and rule options go to the shared pricer (assets/sv-pdp-price.js),
  // so pack-remainder, cap, step and 99p rules match the button and cart. The
  // pack radios already hold the selected variant's prices (updatePackPrices).
  const totalForQuantity = (root, quantity) => {
    const packSelection = root.querySelector('.pack-selection');
    if (!packSelection || !Number.isInteger(quantity) || quantity < 1) return null;

    const packRows = [...packSelection.querySelectorAll('input[data-pack_price_pence]')].map((input) => ({
      quantity: input.value,
      packPricePence: input.dataset.pack_price_pence,
      standardPackPricePence: input.dataset.standard_pack_price_pence,
      exactOnly: input.dataset.exact_only,
      noDiscount: input.dataset.no_discount
    }));

    const pricer = window.SVPdpPrice;
    if (!pricer?.computeTotalPence) {
      // Without the shared pricer only exact packs are trustworthy.
      const exact = packRows.find((row) => Number(row.quantity) === quantity);
      return exact ? Number(exact.packPricePence) : null;
    }

    const autoTokens = String(packSelection.dataset.autoExtraQuantities || '').split(',');
    const marker = root.querySelector('.js-sv-99p');
    const blend99p = autoTokens.includes('all')
      || autoTokens.map(Number).filter(Number.isInteger).includes(quantity);

    return pricer.computeTotalPence(packRows, quantity, {
      capUnits: packSelection.dataset.capUnits,
      specialPricePence: marker?.dataset.specialPricePence,
      blend99p,
      packRemainder: packSelection.dataset.packRemainder === 'true',
      stepPricing: packSelection.dataset.stepPricing === 'true',
      offPackBase: packSelection.dataset.offPackBase === 'true',
      baseUnitPence: packSelection.dataset.baseUnitPence
    });
  };

  // The large price and the sticky bar price show the total for the selected
  // variant and quantity. This script is their only writer; when no
  // trustworthy total exists they are left unchanged, like the main button.
  const updateDisplayPrice = (root) => {
    const form = root.querySelector('.js-product-form-main');
    const quantity = Number.parseInt(form?.querySelector('[name="quantity"]')?.value, 10) || 1;
    const pence = totalForQuantity(root, quantity);
    if (pence == null || !Number.isFinite(Number(pence))) return;

    const price = formatMoney(pence);
    const display = root.querySelector('[data-advent-price]');
    if (display) display.textContent = price;

    const section = root.closest('.cc-main-product') || document;
    section.querySelectorAll('.sticky-atc-panel--advent [data-advent-sticky-price]').forEach((stickyPrice) => {
      stickyPrice.textContent = price;
    });
  };

  // The sticky button proxies the main one, so it mirrors its disabled state
  // (e.g. sold out) and label. Loading states use aria-disabled and
  // are deliberately not mirrored.
  const syncStickyButton = (root) => {
    const mainButton = root.querySelector('.js-product-form-main [name="add"]');
    const section = root.closest('.cc-main-product') || document;
    const stickyButtons = section.querySelectorAll('[data-advent-sticky-add]');
    if (!mainButton || !stickyButtons.length) return;

    // Variant changes replace the button's spans with plain text.
    const label = (mainButton.querySelector('span') || mainButton).textContent.trim();
    stickyButtons.forEach((button) => {
      button.disabled = mainButton.disabled;
      const stickyLabel = button.querySelector('[data-advent-sticky-label]');
      if (stickyLabel && label) stickyLabel.textContent = label;
    });
  };

  const watchStickyButton = (root) => {
    const mainButton = root.querySelector('.js-product-form-main [name="add"]');
    if (!mainButton) return;
    syncStickyButton(root);
    new MutationObserver(() => syncStickyButton(root)).observe(mainButton, {
      attributes: true,
      attributeFilter: ['disabled'],
      childList: true,
      subtree: true,
      characterData: true
    });
  };

  const updatePackPrices = (root, variant) => {
    const packSelection = root.querySelector('.pack-selection');
    const prices = readPackPrices(root, variant?.id);
    if (!packSelection || !prices) return;

    // snus-pack-selection.liquid renders data-base-unit-pence from the first
    // variant's price; the shared pricer uses it for capped and base-price
    // fallbacks, so it must follow the selected variant too.
    if (Number.isFinite(Number(variant.price))) {
      packSelection.dataset.baseUnitPence = String(variant.price);
    }

    packSelection.querySelectorAll('input[type="radio"]').forEach((input) => {
      const price = prices[String(input.value)];
      if (!price) return;

      input.dataset.variant_price = String(price.unitPrice);
      input.dataset.pack_price_pence = String(price.packPrice);
      input.dataset.standard_pack_price_pence = String(price.packPrice);
      input.dataset.pack_price = formatMoney(price.packPrice);

      const label = input.nextElementSibling;
      const packPrice = label?.querySelector('.pack-option__price');
      const unitPrice = label?.querySelector('.pack-option__unit');
      if (packPrice) packPrice.textContent = formatMoney(price.packPrice);
      if (unitPrice && Number(input.value) > 1) {
        unitPrice.textContent = `${formatMoney(price.unitPrice)}${packSelection.dataset.unitLabel || '/unit'}`;
      }
    });

    updateDisplayPrice(root);

    const selected = packSelection.querySelector('input:checked');
    if (!selected) return;

    const selectedPrice = prices[String(selected.value)];
    if (!selectedPrice) return;

    const buttonPrice = root.querySelector('[data-btn-price]');
    if (buttonPrice) buttonPrice.textContent = formatMoney(selectedPrice.packPrice);

    document.dispatchEvent(new CustomEvent('price-changed', {
      detail: { price: selectedPrice.unitPrice }
    }));
    document.dispatchEvent(new CustomEvent('quantity-changed', {
      detail: { quantity: Number(selected.value) || 1 }
    }));
  };

  const updateUgcControls = (carousel) => {
    const track = carousel.querySelector('[data-advent-ugc-track]');
    const controls = carousel.querySelector('.advent-ugc-carousel__controls');
    const previous = carousel.querySelector('[data-advent-ugc-prev]');
    const next = carousel.querySelector('[data-advent-ugc-next]');
    if (!track || !controls || !previous || !next) return;

    const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
    const canScroll = maxScroll > 2;
    controls.hidden = !canScroll;
    previous.disabled = !canScroll || track.scrollLeft <= 2;
    next.disabled = !canScroll || track.scrollLeft >= maxScroll - 2;
  };

  const scrollUgcCarousel = (carousel, direction) => {
    const track = carousel.querySelector('[data-advent-ugc-track]');
    const item = track?.querySelector('.advent-ugc-carousel__item');
    if (!track || !item) return;

    const trackStyles = window.getComputedStyle(track);
    const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
    const distance = item.getBoundingClientRect().width + gap;
    track.scrollBy({ left: distance * direction, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  const playUgcVideo = (carousel, trigger) => {
    const card = trigger.closest('[data-advent-ugc-video]');
    const player = card?.querySelector('[data-advent-ugc-player]');
    const video = player?.querySelector('video');
    if (!card || !player || !video) return;

    carousel.querySelectorAll('.advent-ugc-card__video').forEach((otherVideo) => {
      if (otherVideo !== video) otherVideo.pause();
    });

    trigger.hidden = true;
    player.hidden = false;
    const playRequest = video.play();
    if (playRequest?.catch) playRequest.catch(() => {});
  };

  const initUgcCarousel = (carousel) => {
    if (carousel.dataset.adventUgcReady === 'true') return;
    carousel.dataset.adventUgcReady = 'true';

    const track = carousel.querySelector('[data-advent-ugc-track]');
    updateUgcControls(carousel);

    track?.addEventListener('scroll', () => {
      window.requestAnimationFrame(() => updateUgcControls(carousel));
    }, { passive: true });

    if ('ResizeObserver' in window && track) {
      const resizeObserver = new ResizeObserver(() => updateUgcControls(carousel));
      resizeObserver.observe(track);
    }

    carousel.addEventListener('click', (event) => {
      const playTrigger = event.target.closest('[data-advent-ugc-play]');
      if (playTrigger) {
        playUgcVideo(carousel, playTrigger);
        return;
      }

      if (event.target.closest('[data-advent-ugc-prev]')) {
        scrollUgcCarousel(carousel, -1);
      } else if (event.target.closest('[data-advent-ugc-next]')) {
        scrollUgcCarousel(carousel, 1);
      }
    });
  };

  // Gallery: the product's media, swiped or stepped with the arrows. It is
  // independent of the selected variant; the dots follow the visible slide.
  const initGallery = (root) => {
    const gallery = root.querySelector('[data-advent-gallery]');
    const track = gallery?.querySelector('[data-advent-gallery-track]');
    if (!gallery || !track || gallery.dataset.adventGalleryReady === 'true') return;
    gallery.dataset.adventGalleryReady = 'true';

    const slides = Array.from(track.querySelectorAll('[data-advent-gallery-slide]'));
    const dots = Array.from(gallery.querySelectorAll('[data-advent-gallery-dot]'));
    if (slides.length < 2) return;

    let activeIndex = 0;
    let settleTimer = null;

    const markActive = (index) => {
      activeIndex = index;
      slides.forEach((slide, slideIndex) => {
        if (slideIndex === index) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });
      dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
    };

    const scrollToIndex = (index, smooth = true) => {
      const slide = slides[index];
      if (!slide) return;
      markActive(index);
      track.scrollTo({ left: slide.offsetLeft, behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto' });
    };

    const indexFromScroll = () => {
      const width = track.clientWidth || 1;
      return Math.min(Math.max(Math.round(track.scrollLeft / width), 0), slides.length - 1);
    };

    // Once scrolling settles (swipe or arrow), the dots follow the visible slide.
    track.addEventListener('scroll', () => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        const index = indexFromScroll();
        if (index !== activeIndex) markActive(index);
      }, 120);
    }, { passive: true });

    gallery.addEventListener('click', (event) => {
      const step = event.target.closest('[data-advent-gallery-next]') ? 1
        : event.target.closest('[data-advent-gallery-prev]') ? -1 : 0;
      if (!step) return;
      scrollToIndex((activeIndex + step + slides.length) % slides.length);
    });

    if ('ResizeObserver' in window) {
      new ResizeObserver(() => scrollToIndex(activeIndex, false)).observe(track);
    }
  };

  const initBrands = (root) => {
    root.querySelectorAll('[data-advent-brands]').forEach((brands) => {
      const track = brands.querySelector('[data-advent-brands-track]');
      const next = brands.querySelector('[data-advent-brands-next]');
      if (!track || !next) return;

      const update = () => {
        const maxScroll = track.scrollWidth - track.clientWidth;
        next.hidden = maxScroll <= 2 || track.scrollLeft >= maxScroll - 2;
      };

      next.addEventListener('click', () => {
        track.scrollBy({ left: track.clientWidth * 0.8, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      });
      track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
      update();
    });
  };

  const init = (root) => {
    if (root.dataset.adventCalendarReady === 'true') return;
    root.dataset.adventCalendarReady = 'true';

    root.querySelectorAll('[data-advent-ugc-carousel]').forEach(initUgcCarousel);
    initGallery(root);
    initBrands(root);

    root.addEventListener('on:variant:change', (event) => {
      updatePackPrices(root, event.detail?.variant);
    });

    root.addEventListener('change', (event) => {
      if (event.target.closest('.pack-selection')) updateDisplayPrice(root);
    });
    // The stepper can set quantities outside 1/3/5; keep the totals in step.
    const quantityInput = root.querySelector('.js-product-form-main [name="quantity"]');
    ['input', 'change'].forEach((type) => quantityInput?.addEventListener(type, () => updateDisplayPrice(root)));
    document.addEventListener('quantity-changed', () => {
      window.requestAnimationFrame(() => updateDisplayPrice(root));
    });
    watchStickyButton(root);
    document.addEventListener('pack-selection-changed', () => {
      window.requestAnimationFrame(() => updateDisplayPrice(root));
    });
    updateDisplayPrice(root);
  };

  // The sticky bar sits outside the product block, so it submits the main form
  // and reuses its variant, quantity and loading states.
  document.addEventListener('click', (event) => {
    const stickyAdd = event.target.closest('[data-advent-sticky-add]');
    if (!stickyAdd) return;
    const section = stickyAdd.closest('.cc-main-product') || document;
    section.querySelector('.product--advent-calendar-2026 .js-product-form-main [name="add"]')?.click();
  });

  const initAll = (scope = document) => {
    scope.querySelectorAll('.product--advent-calendar-2026').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
